import React from 'react'
import type { Block } from '../../types'
import { PropertyLabel, PropertyInput, ToggleSwitch } from './shared'
import { Plus, Trash2, GripVertical, CircleCheck } from 'lucide-react'

interface QuizPropertiesProps {
  block: Block
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

interface QuizOption {
  label: string
  value: string
}

export default function QuizProperties({ block, onUpdateBlock }: QuizPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const options = (config.options as QuizOption[]) || []
  const correctValue = (config.correctValue as string) || ''
  const explanation = (config.explanation as string) || ''
  const showExplanation = (config.showExplanation as boolean) ?? true
  const allowRetry = (config.allowRetry as boolean) ?? true

  const updateConfig = (updates: Record<string, unknown>) => {
    onUpdateBlock(block.id, { config: { ...config, ...updates } })
  }

  const updateOptions = (newOptions: QuizOption[]) => {
    updateConfig({ options: newOptions })
  }

  const updateOption = (index: number, field: 'label' | 'value', val: string) => {
    const newOptions = options.map((opt, i) =>
      i === index ? { ...opt, [field]: val } : opt
    )
    updateOptions(newOptions)
  }

  const addOption = () => {
    const num = options.length + 1
    updateOptions([...options, { label: `Option ${num}`, value: `option_${num}` }])
  }

  const removeOption = (index: number) => {
    if (options.length <= 2) return
    const removed = options[index]
    const newOptions = options.filter((_, i) => i !== index)
    updateConfig({
      options: newOptions,
      ...(removed.value === correctValue ? { correctValue: newOptions[0]?.value || '' } : {}),
    })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Question</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
          placeholder="What is the capital of France?"
        />
      </div>

      <div className="space-y-2">
        <PropertyLabel>Options</PropertyLabel>
        <div className="space-y-2">
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <button
                onClick={() => updateConfig({ correctValue: opt.value })}
                className={`p-0.5 rounded-full transition-colors shrink-0 ${
                  correctValue === opt.value
                    ? 'text-emerald-500'
                    : 'text-slate-300 hover:text-slate-400'
                }`}
                title={correctValue === opt.value ? 'Correct answer' : 'Mark as correct'}
              >
                <CircleCheck className="h-4 w-4" />
              </button>
              <input
                type="text"
                value={opt.label}
                onChange={(e) => updateOption(i, 'label', e.target.value)}
                placeholder="Label"
                className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none"
              />
              <button
                onClick={() => removeOption(i)}
                disabled={options.length <= 2}
                className="p-1.5 hover:bg-slate-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Remove option"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={addOption}
          className="w-full py-2 border border-dashed border-slate-300 text-slate-500 text-xs font-bold uppercase tracking-wider rounded-lg hover:border-[#5048e5] hover:text-[#5048e5] transition-all flex items-center justify-center gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Option
        </button>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Explanation</PropertyLabel>
        <textarea
          value={explanation}
          onChange={(e) => updateConfig({ explanation: e.target.value })}
          placeholder="Explain the correct answer..."
          rows={3}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent transition-all outline-none resize-none"
        />
      </div>

      <div className="flex items-center justify-between">
        <PropertyLabel>Show Explanation</PropertyLabel>
        <ToggleSwitch
          checked={showExplanation}
          onChange={(v) => updateConfig({ showExplanation: v })}
        />
      </div>

      <div className="flex items-center justify-between">
        <PropertyLabel>Allow Retry</PropertyLabel>
        <ToggleSwitch
          checked={allowRetry}
          onChange={(v) => updateConfig({ allowRetry: v })}
        />
      </div>

      {block.variable && (
        <div className="space-y-1.5">
          <PropertyLabel>Variable</PropertyLabel>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
            <span className="font-mono text-sm text-[#5048e5]">@{block.variable}</span>
            <span className="text-[10px] text-slate-400 ml-auto">Use in visibility rules</span>
          </div>
        </div>
      )}
    </div>
  )
}
